import React from 'react'
import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'

import {
  ANALYTICS_EVENTS,
  Drawer,
  Loader,
  useAccountStore,
  useAppConfig,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useTransactionStore,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { AccountType, WidgetProps } from '../core'

import { RecurringSettings } from '../recurringtransactions'

import CashflowContent from './components/CashflowContent'
import CashflowHeading from './components/CashflowHeading'
import { useCashflowUiStore } from './context/hooks'
import { subDays } from 'date-fns/subDays'
import { startOfToday } from 'date-fns/startOfToday'
import { endOfToday } from 'date-fns/endOfToday'

const CashflowWidget: React.FC<WidgetProps> = ({ onBackClick, onMenuClick, sx = {} }) => {
  const { selectedAccounts, selectedAccountGuids, shouldDisableNextDate, shouldDisablePrevDate } =
    useGlobalUiStore()

  const { onEvent } = useEvent()
  const { clientConfig } = useAppConfig()
  const { visibleCashAccounts: cashAccounts } = useAccountStore()
  const { setFilter } = useTransactionStore()
  const { dateRange } = useCashflowUiStore()
  const { alert, loadRepeatingTransactionData, isDataLoaded, setAlert } =
    useRecurringTransactionsStore()
  const { cashflow: customCopy, recurring: recurringCopy } = useGlobalCopyStore()
  const { isInitialized } = useGlobalUiStore()

  const view = clientConfig?.master?.deep_link_params?.view
  const isManageIncomeView = view === 'manage_income'
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(isManageIncomeView)

  React.useEffect(() => {
    if (!isDataLoaded) {
      loadRepeatingTransactionData().finally()
    }
  }, [])

  React.useEffect(() => {
    const cashAccountGuids = cashAccounts.map((account) => account.guid)
    const selAccts = selectedAccounts.filter(
      (account) =>
        selectedAccountGuids.includes(account.guid) && cashAccountGuids.includes(account.guid),
    )
    setFilter({
      dateRange: { start: subDays(startOfToday(), 90), end: endOfToday() },
      accounts: selAccts.map((a) => a.guid),
    })
  }, [cashAccounts, selectedAccounts])

  useWidgetLoadTimer({
    widgetName: 'CashflowWidget',
    isLoaded: isDataLoaded,
  })

  if (!isInitialized) return <Loader />

  return (
    <WidgetContainer
      actions={[
        {
          label: recurringCopy.action_title,
          iconName: 'credit_card_gear',
          onClick: () => {
            setIsSettingsOpen(true)
            onEvent(ANALYTICS_EVENTS.CASH_FLOW_CLICK_SETTINGS)
          },
        },
      ]}
      allowedAccountTypes={[
        AccountType.CASH,
        AccountType.CHECKING,
        AccountType.PREPAID,
        AccountType.SAVINGS,
        AccountType.CHECKING_LINE_OF_CREDIT,
      ]}
      calendarActions={{
        shouldDisableNext: shouldDisableNextDate,
        shouldDisablePrevious: shouldDisablePrevDate,
        shouldDisablePicker: true,
        onRangeChanged: () => {},
      }}
      dateRange={dateRange}
      dateRangeVariant="readonly"
      hasDivider={true}
      onAccountsFilterClick={() => onEvent(ANALYTICS_EVENTS.CASH_FLOW_CLICK_FILTER)}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={customCopy.title}
    >
      <CashflowHeading />

      <CashflowContent />

      <Drawer
        ariaLabelClose={customCopy.close_settings_aria}
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        shouldShowHeaderBorders={false}
        title={recurringCopy.action_title}
      >
        <RecurringSettings defaultSelectedTab={isManageIncomeView ? 1 : 0} />
      </Drawer>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3500}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return
          setAlert('', alert.severity)
        }}
        open={Boolean(alert.message)}
      >
        <Alert
          onClose={() => setAlert('', alert.severity)}
          severity={alert.severity}
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </WidgetContainer>
  )
}

export default observer(CashflowWidget)
